// RegisterPropertyScreen.tsx
import React, { useEffect } from 'react';
import { Styles } from './style';
import Header from '@components/Header';
import Navbar from '@components/Navbar';
import AppBanner from '@components/Banner/appBanner';
import Footer from '@components/Footer/Footer';
import { useMediaQuery, useTheme } from '@mui/material';
import { useViaCepStore } from '@stores/ViaCep/ViaCep';
import { useCadastroImovelStore } from '@stores/CadastroImovel/CadastroImovel';
import { useLocalidadesIbgeStore } from '@stores/LocalidadesIbge/LocalidadesIbge';
import { maskPhone, isValidPhone, isValidEmail } from '@utils/masks';
import FeedbackModal from '@components/FeedbackModal';
import Loading from '@components/Loading/Loading';
import SEO from '@components/SEO';
import { useLocation } from 'react-router-dom';

interface FormData {
  nome: string;
  email: string;
  telefone: string;
  condicao: string;
  tipo: string;
  dormitorios: string;
  suites: string;
  descricao: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  informacoes_adicionais: string;
}

const RegisterPropertyScreen: React.FC = () => {
  const location = useLocation();
  const { fetchCep } = useViaCepStore();
  const { submitCadastroImovel, loading, error, lastResponse } =
    useCadastroImovelStore();
  const {
    municipios,
    loading: loadingCidades,
    fetchDistritosByUF
  } = useLocalidadesIbgeStore();
  const [phoneError, setPhoneError] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [modal, setModal] = React.useState<{
    open: boolean;
    variant: 'success' | 'error';
    title: string;
    message: string;
  }>({
    open: false,
    variant: 'success',
    title: '',
    message: ''
  });

  const [formData, setFormData] = React.useState<FormData>({
    nome: '',
    email: '',
    telefone: '',
    condicao: '',
    tipo: '',
    dormitorios: '',
    suites: '',
    descricao: '',
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    informacoes_adicionais: ''
  });

  const handleChange = async (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    if (name === 'cep') {
      const onlyDigits = value.replace(/\D/g, '');
      if (onlyDigits.length === 8) {
        const data = await fetchCep(value);
        if (data) {
          if (data.uf) fetchDistritosByUF(data.uf);
          setFormData((prevState: FormData) => ({
            ...prevState,
            rua: data.logradouro || '',
            bairro: data.bairro || '',
            cidade: data.localidade || '',
            uf: data.uf || ''
          }));
        }
      }
    }

    if (name === 'uf') {
      fetchDistritosByUF(value);
      setFormData((prev) => ({ ...prev, uf: value, cidade: '' }));
      return;
    }

    if (name === 'telefone') {
      const masked = maskPhone(value);
      setFormData((prev) => ({ ...prev, telefone: masked }));
      if (phoneError)
        setPhoneError(!masked || isValidPhone(masked) ? '' : phoneError);
      return;
    }

    if (name === 'email') {
      setFormData((prev) => ({ ...prev, email: value }));
      if (emailError) setEmailError(isValidEmail(value) ? '' : emailError);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneBlur = () => {
    if (formData.telefone && !isValidPhone(formData.telefone)) {
      setPhoneError('Telefone inválido. Use (XX) XXXX-XXXX ou (XX) XXXXX-XXXX');
    } else {
      setPhoneError('');
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !isValidEmail(formData.email)) {
      setEmailError('E-mail inválido');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pErr =
      formData.telefone && !isValidPhone(formData.telefone)
        ? 'Telefone inválido. Use (XX) XXXX-XXXX ou (XX) XXXXX-XXXX'
        : '';
    const eErr = !isValidEmail(formData.email) ? 'E-mail inválido' : '';
    setPhoneError(pErr);
    setEmailError(eErr);
    if (pErr || eErr) return;

    const payload = {
      nome: formData.nome,
      email: formData.email,
      telefone: formData.telefone || undefined,
      condicao: formData.condicao || undefined,
      tipo: formData.tipo || undefined,
      dormitorios: formData.dormitorios,
      suites: formData.suites,
      descricao: formData.descricao || undefined,
      cep: formData.cep,
      uf: formData.uf,
      cidade: formData.cidade,
      bairro: formData.bairro,
      rua: formData.rua,
      numero: formData.numero,
      informacoes_adicionais:
        formData.informacoes_adicionais || formData.complemento || undefined
    };

    const resp = await submitCadastroImovel(payload);
    if (resp) {
      setFormData((prev) => ({
        ...prev,
        nome: '',
        email: '',
        telefone: '',
        condicao: '',
        tipo: '',
        dormitorios: '',
        suites: '',
        descricao: '',
        cep: '',
        rua: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        uf: '',
        informacoes_adicionais: ''
      }));
      setModal({
        open: true,
        variant: 'success',
        title: 'Cadastro enviado!',
        message:
          'Recebemos os dados do seu imóvel e entraremos em contato em breve para dar continuidade ao processo.'
      });
    } else {
      setModal({
        open: true,
        variant: 'error',
        title: 'Falha no envio',
        message:
          'Não foi possível registrar seu imóvel. Verifique os dados e tente novamente.'
      });
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0 });
    }, 100);
  }, [location]);

  return (
    <>
      <SEO
        title="Cadastre seu Imóvel"
        description="Cadastre seu imóvel na Elite Prime Imobiliária e encontre o melhor negócio. Anuncie casas, apartamentos e terrenos com a melhor equipe de corretores de Sorocaba."
      />
      {loading && <Loading />}
      <FeedbackModal
        open={modal.open}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
      />
      <Header />
      <Navbar />
      <Styles.Main>
        <Styles.LabelTipo>
          Cadastre seu imóvel no formulário abaixo que encontraremos o melhor
          negócio para você.
        </Styles.LabelTipo>

        <Styles.Container>
          <form onSubmit={handleSubmit}>
            <Styles.SectionTitle>Dados Pessoais</Styles.SectionTitle>
            <Styles.FormRow>
              <Styles.InputContainer>
                <Styles.Label className="required">Nome</Styles.Label>
                <Styles.Input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                />
              </Styles.InputContainer>

              <Styles.InputContainer width="30%">
                <Styles.Label className="required">E-mail</Styles.Label>
                <Styles.Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  required
                />
                {emailError && (
                  <small
                    style={{
                      color: '#d32f2f',
                      fontSize: '12px',
                      marginTop: '2px'
                    }}>
                    {emailError}
                  </small>
                )}
              </Styles.InputContainer>

              <Styles.InputContainer width="10%">
                <Styles.Label>Telefone</Styles.Label>
                <Styles.Input
                  type="text"
                  name="telefone"
                  placeholder="(XX) XXXXX-XXXX"
                  value={formData.telefone}
                  onChange={handleChange}
                  onBlur={handlePhoneBlur}
                />
                {phoneError && (
                  <small
                    style={{
                      color: '#d32f2f',
                      fontSize: '12px',
                      marginTop: '2px'
                    }}>
                    {phoneError}
                  </small>
                )}
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.SectionTitle>Sobre o Imóvel</Styles.SectionTitle>
            <Styles.FormRow>
              <Styles.InputContainer>
                <Styles.Label>Condição do Imóvel</Styles.Label>
                <Styles.Select
                  as="select"
                  name="condicao"
                  value={formData.condicao}
                  onChange={handleChange}>
                  <option value="">Selecione</option>
                  <option value="NOVO">Novo</option>
                  <option value="USADO">Usado</option>
                  <option value="EM_OBRAS">Em obras</option>
                </Styles.Select>
              </Styles.InputContainer>

              <Styles.InputContainer>
                <Styles.Label>Tipo do Imóvel</Styles.Label>
                <Styles.Select
                  as="select"
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}>
                  <option value="">Selecione</option>
                  <option value="CASA">Casa</option>
                  <option value="APARTAMENTO">Apartamento</option>
                  <option value="TERRENO">Terreno</option>
                  <option value="COMERCIAL">Comercial</option>
                </Styles.Select>
              </Styles.InputContainer>

              <Styles.InputContainer>
                <Styles.Label>Dormitórios</Styles.Label>
                <Styles.Select
                  as="select"
                  name="dormitorios"
                  value={formData.dormitorios}
                  onChange={handleChange}>
                  <option value="">Quantidade</option>
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </Styles.Select>
              </Styles.InputContainer>

              <Styles.InputContainer>
                <Styles.Label>Suítes</Styles.Label>
                <Styles.Select
                  as="select"
                  name="suites"
                  value={formData.suites}
                  onChange={handleChange}>
                  <option value="">Quantidade</option>
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </Styles.Select>
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.FormRow>
              <Styles.InputContainer fullWidth>
                <Styles.Label>Descrição</Styles.Label>
                <Styles.TextArea
                  name="descricao"
                  placeholder="Descrição detalhada"
                  value={formData.descricao}
                  onChange={handleChange}
                />
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.SectionTitle>Endereço</Styles.SectionTitle>
            <Styles.FormRow>
              <Styles.InputContainer width="15%">
                <Styles.Label className="required">CEP</Styles.Label>
                <Styles.Input
                  type="text"
                  name="cep"
                  value={formData.cep}
                  onChange={handleChange}
                  required
                />
              </Styles.InputContainer>
              <Styles.InputContainer>
                <Styles.Label className="required">Rua</Styles.Label>
                <Styles.Input
                  type="text"
                  name="rua"
                  value={formData.rua}
                  onChange={handleChange}
                  required
                />
              </Styles.InputContainer>

              <Styles.InputContainer width="fit-content">
                <Styles.Label className="required">Número</Styles.Label>
                <Styles.Input
                  type="text"
                  name="numero"
                  value={formData.numero}
                  onChange={handleChange}
                  required
                  style={{ width: 100 }}
                />
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.FormRow>
              <Styles.InputContainer>
                <Styles.Label>Complemento</Styles.Label>
                <Styles.Input
                  type="text"
                  name="complemento"
                  value={formData.complemento}
                  onChange={handleChange}
                />
              </Styles.InputContainer>

              <Styles.InputContainer>
                <Styles.Label className="required">Bairro</Styles.Label>
                <Styles.Input
                  type="text"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleChange}
                  required
                />
              </Styles.InputContainer>

              <Styles.InputContainer width="5%">
                <Styles.Label className="required">UF</Styles.Label>
                <Styles.Select
                  as="select"
                  name="uf"
                  value={formData.uf}
                  onChange={handleChange}
                  required>
                  <option value="">Selecione</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MT">MT</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RS">RS</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </Styles.Select>
              </Styles.InputContainer>

              <Styles.InputContainer width="15%">
                <Styles.Label className="required">Cidade</Styles.Label>
                <Styles.Select
                  as="select"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  required
                  disabled={!formData.uf || loadingCidades}>
                  <option value="">
                    {loadingCidades ? 'Carregando...' : 'Selecione'}
                  </option>
                  {municipios.map((m) => (
                    <option key={m.id} value={m.nome}>
                      {m.nome}
                    </option>
                  ))}
                </Styles.Select>
              </Styles.InputContainer>
            </Styles.FormRow>

            <Styles.FormRow>
              <Styles.InputContainer fullWidth>
                <Styles.Label>Informações Adicionais</Styles.Label>
                <Styles.TextArea
                  name="informacoes_adicionais"
                  placeholder="Alguma informação extra..."
                  value={formData.informacoes_adicionais}
                  onChange={handleChange}
                />
              </Styles.InputContainer>
            </Styles.FormRow>

            {error && <p style={{ color: 'red', marginTop: 10 }}>{error}</p>}
            {lastResponse && (
              <p style={{ color: 'green', marginTop: 10 }}>
                {lastResponse.message}
              </p>
            )}

            <Styles.LabelTipo
              style={{
                fontSize: '16px',
                marginBottom: '30px',
                marginTop: '25px'
              }}>
              * Obrigatório
            </Styles.LabelTipo>
            <Styles.Button type="submit" disabled={loading}>
              {loading ? 'Enviando...' : 'ENVIAR'}
            </Styles.Button>
          </form>
          {!isMobile && <AppBanner />}
        </Styles.Container>
      </Styles.Main>
      <Footer />
    </>
  );
};

export default RegisterPropertyScreen;
